import { Combobox } from '@headlessui/react';
import useDebounce from '@kk/shared/hooks/useDebounce';
import { cn } from 'cn';
import {
  type FocusEvent,
  type KeyboardEvent,
  type Ref,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import type api from '#/api';
import {
  MIN_SEARCH_QUERY_LENGTH,
  useSearchResults,
} from '#/api/hooks/useSearchResults';
import { generateUrl } from '#/routes/generateUrl';
import { SearchInput } from './SearchInput';
import { SearchResults } from './SearchResults';
import { type SavedSearch, useSavedSearch } from './useSavedSearch';

type Props = {
  className?: string;
  ref?: Ref<HTMLInputElement>;
  onBlur?: () => void;
};

/**
 * @url https://kommunekredit.visualstudio.com/KK%20Laaneportal/_wiki/wikis/KK-Laaneportal.wiki/1917/Global-s%C3%B8gning
 */
export function Search({ className, ref, onBlur }: Props): React.ReactNode {
  const [open, setOpen] = useState(false);
  const [showOptionFocus, setShowOptionFocus] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const { savedSearches, addSavedSearch } = useSavedSearch();
  const navigate = useNavigate();

  const [inputValue, setInputValue] = useState<string>('');
  const defferedInput = useDeferredValue(inputValue);
  const query = useDebounce<string>(defferedInput, 200, {
    leading: true,
    trailing: true,
    maxWait: 700,
  });

  const { data } = useSearchResults(
    { query },
    {
      enabled: query.length >= MIN_SEARCH_QUERY_LENGTH,
    },
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  function handleValueSelect(value: api.LoanDocument | SavedSearch | null) {
    setOpen(false);
    setShowOptionFocus(false);
    if (!value) return;
    const { contractNumber, loanType, maturityDate } = value;
    if (!contractNumber || !loanType || !maturityDate) return;
    addSavedSearch({
      contractNumber,
      loanType,
      maturityDate,
    });
    // go to selected entity page (loan, case, company)
    navigate(
      generateUrl('/aftaler/:contractId', { contractId: contractNumber }),
    );
    setInputValue('');
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  function handleFocus() {
    if (open) return;
    setShowOptionFocus(false);
    setOpen(true);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      setShowOptionFocus(true);
    }
  }

  function handleKeyUp(event: KeyboardEvent<HTMLInputElement>) {
    if (!open) {
      setOpen(true);
    }
    if (event.key !== 'Escape') return;

    event.preventDefault();
    event.stopPropagation();
    setOpen(false);
  }

  function handleBlur(event: FocusEvent<HTMLInputElement, Element>) {
    if (
      event.relatedTarget instanceof HTMLElement &&
      event.relatedTarget.getAttribute('role') === 'option'
    ) {
      return;
    }
    if (
      event.relatedTarget instanceof HTMLButtonElement &&
      event.relatedTarget.getAttribute('name') === 'clear'
    ) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    window.requestAnimationFrame(() => {
      setOpen(false);
      onBlur?.();
    });
  }

  return (
    <Combobox
      as="div"
      className={cn('relative', className)}
      ref={wrapperRef}
      value={null}
      onChange={handleValueSelect}
      immediate
    >
      <SearchInput
        open={open}
        value={inputValue}
        ref={ref}
        onBlur={handleBlur}
        onChange={(value) => {
          setInputValue(value);
          setShowOptionFocus(false);
        }}
        onClose={() => setOpen(false)}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
      />
      <SearchResults
        open={open}
        query={query}
        data={data}
        savedSearches={savedSearches}
        onOptionInteraction={() => setShowOptionFocus(true)}
        showOptionFocus={
          showOptionFocus || query.length >= MIN_SEARCH_QUERY_LENGTH
        }
      />
    </Combobox>
  );
}
